import Phaser from 'phaser';
import { Key9Scene } from './game/Key9Scene';
import type { Key9Puzzle, Key9Result } from './types/puzzle';
import { validatePuzzleShape, resultLabel } from './logic/key9Validator';
import {
  loadSave,
  saveFullDone,
  saveFullProgress,
  saveKey9Missed,
  saveKey9Unlock,
  type DayProgress
} from './storage/localSave';
import {
  buildFullShareText,
  buildPracticeShareText,
  buildShareCardModel,
  buildShareText,
  formatTime,
  type ShareCardModel,
  type ShareCardVariant
} from './logic/share';
import {
  getInitialDayFlow,
  getInlineKey9Actions,
  getMissedPanelCopy,
  getResultActionSheet,
  getTerminalResultLayout,
  type InlineKey9ActionState,
  type ResultActionSheetState
} from './logic/dayFlow';
import { getGameControlState, type GameControlPhase } from './logic/gameControls';

let game: Phaser.Game | null = null;
let currentPuzzle: Key9Puzzle | null = null;
let timerId: number | null = null;
let startMs = Date.now();
let latestKey9Result: Key9Result | null = null;
let latestProgress: DayProgress | null = null;
let latestPracticeResult: Key9Result | null = null;
let latestShareVariant: ShareCardVariant | null = null;
let lastMisses = 0;
let buttonsBound = false;
let eventsBound = false;

const containerId = 'daysudoku-game';

async function boot() {
  const container = document.getElementById(containerId);
  if (!container) return;
  bindButtons();
  bindEvents();

  currentPuzzle = await loadTodayPuzzle();
  const errors = validatePuzzleShape(currentPuzzle);
  if (errors.length) {
    console.error('Invalid puzzle:', errors);
    container.textContent = 'Puzzle data error.';
    return;
  }

  const save = loadSave();
  const progress = save.days[currentPuzzle.date] ?? null;
  latestProgress = progress;
  latestKey9Result = progress?.key9Result ?? save.results[currentPuzzle.date] ?? null;

  const flow = getInitialDayFlow(progress);
  startMs = Date.now();
  game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: containerId,
    width: 430,
    height: 630,
    backgroundColor: '#f5f7fb',
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH
    },
    scene: [Key9Scene]
  });
  game.scene.start('Key9Scene', { puzzle: currentPuzzle, mode: flow.mode, progress: progress ?? undefined, practice: flow.practice });
  setStatusLabel(flow.statusLabel);
  updateGameControls(flow.mode === 'full' ? 'full_in_progress' : flow.practice ? 'key9_practice' : 'key9_in_progress');

  if (flow.panel === 'full_done' && progress) {
    showFullDone(progress, save.currentStreak);
    stopTimer();
    setMisses(progress.key9Result?.misses ?? 0);
    const timerEl = document.getElementById('ds-timer');
    if (timerEl) timerEl.textContent = formatTime(progress.fullSeconds ?? 0);
    return;
  }

  if (flow.panel === 'missed' && latestKey9Result) {
    showKey9Missed(latestKey9Result);
    stopTimer();
    setMisses(latestKey9Result.misses);
    return;
  }

  startTimer();
}

async function loadTodayPuzzle(): Promise<Key9Puzzle> {
  const today = localDateKey();
  const year = today.slice(0, 4);
  const urls = [`/puzzles/${year}/${today}.json`, '/puzzles/2026/2026-06-12.json'];
  for (const url of urls) {
    try {
      const res = await fetch(url, { cache: 'no-cache' });
      if (res.ok) return await res.json() as Key9Puzzle;
    } catch {
      // try next fallback
    }
  }
  throw new Error('No puzzle found');
}

function localDateKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function bindButtons() {
  if (buttonsBound) return;
  buttonsBound = true;
  document.getElementById('ds-undo')?.addEventListener('click', () => getScene()?.undo());
  document.getElementById('ds-erase')?.addEventListener('click', () => getScene()?.erase());
  document.getElementById('ds-notes')?.addEventListener('click', () => {
    const enabled = getScene()?.toggleNotes() ?? false;
    setNotesButtonState(enabled);
  });
  document.getElementById('ds-hint')?.addEventListener('click', () => getScene()?.hint());
  document.getElementById('ds-share')?.addEventListener('click', shareLatest);
  document.getElementById('ds-sheet-share')?.addEventListener('click', shareLatest);
  document.getElementById('ds-inline-share')?.addEventListener('click', shareLatest);
  document.getElementById('ds-practice')?.addEventListener('click', startKey9Practice);
  document.getElementById('ds-sheet-practice')?.addEventListener('click', startKey9Practice);
  document.getElementById('ds-inline-practice')?.addEventListener('click', startKey9Practice);
  document.getElementById('ds-continue')?.addEventListener('click', continueSudoku);
  document.getElementById('ds-sheet-continue')?.addEventListener('click', continueSudoku);
  document.getElementById('ds-inline-continue')?.addEventListener('click', continueSudoku);
}

function bindEvents() {
  if (eventsBound) return;
  eventsBound = true;
  window.addEventListener('daysudoku:key9-status', ((event: Event) => {
    const detail = (event as CustomEvent).detail as { misses: number; phase?: GameControlPhase; notesMode?: boolean; canHint?: boolean };
    const increased = detail.misses > lastMisses;
    setMisses(detail.misses);
    if (increased) {
      const missEl = document.getElementById('ds-misses');
      missEl?.classList.remove('miss-pop');
      window.setTimeout(() => missEl?.classList.add('miss-pop'), 0);
    }
    lastMisses = detail.misses;
    if (detail.phase) updateGameControls(detail.phase, detail.notesMode ?? false, detail.canHint ?? false);
    if (detail.phase === 'full_in_progress') setStatusLabel('Continue today’s Sudoku');
    if (detail.phase === 'key9_practice') setStatusLabel('Key9 Practice');
  }) as EventListener);

  window.addEventListener('daysudoku:key9-unlocked', ((event: Event) => onKey9Unlocked((event as CustomEvent).detail as Key9Result)) as EventListener);
  window.addEventListener('daysudoku:key9-missed', ((event: Event) => onKey9Missed((event as CustomEvent).detail as Key9Result)) as EventListener);
  window.addEventListener('daysudoku:key9-practice-unlocked', ((event: Event) => onKey9PracticeUnlocked((event as CustomEvent).detail as Key9Result)) as EventListener);
  window.addEventListener('daysudoku:key9-practice-missed', (() => {
    if (latestKey9Result) showKey9Missed(latestKey9Result);
    stopTimer();
  }) as EventListener);
  window.addEventListener('daysudoku:full-progress', ((event: Event) => {
    const detail = (event as CustomEvent).detail as { board: string[]; notes?: string[]; fullHints?: number };
    if (!currentPuzzle) return;
    const saved = saveFullProgress(currentPuzzle.date, detail.board, detail.notes, detail.fullHints);
    latestProgress = saved.days[currentPuzzle.date];
  }) as EventListener);
  window.addEventListener('daysudoku:full-complete', ((event: Event) => {
    const detail = (event as CustomEvent).detail as { board: string[]; notes?: string[]; fullHints?: number; seconds: number };
    onFullComplete(detail.board, detail.seconds, detail.notes, detail.fullHints);
  }) as EventListener);
}

function getScene(): Key9Scene | null {
  if (!game) return null;
  return game.scene.getScene('Key9Scene') as Key9Scene;
}

function startTimer() {
  if (timerId) window.clearInterval(timerId);
  timerId = window.setInterval(() => {
    const seconds = Math.round((Date.now() - startMs) / 1000);
    const el = document.getElementById('ds-timer');
    if (el) el.textContent = formatTime(seconds);
  }, 1000);
}

function stopTimer() {
  if (timerId) window.clearInterval(timerId);
  timerId = null;
}

function setMisses(misses: number) {
  const missEl = document.getElementById('ds-misses');
  if (missEl) missEl.textContent = `Misses ${misses}/9`;
}

function startKey9Practice() {
  hideResultPanel();
  latestPracticeResult = null;
  latestShareVariant = null;
  getScene()?.retryKey9Practice();
  startMs = Date.now();
  startTimer();
  setStatusLabel('Key9 Practice');
  setMisses(0);
  updateGameControls('key9_practice');
}

function continueSudoku() {
  hideResultPanel();
  latestPracticeResult = null;
  latestShareVariant = null;
  getScene()?.continueSudoku();
  startMs = Date.now();
  startTimer();
  setStatusLabel('Continue today’s Sudoku');
  updateGameControls('full_in_progress');
}

function onKey9Unlocked(result: Key9Result) {
  stopTimer();
  latestKey9Result = result;
  latestPracticeResult = null;
  const saved = currentPuzzle ? saveKey9Unlock(currentPuzzle.date, result) : loadSave();
  latestProgress = currentPuzzle ? saved.days[currentPuzzle.date] : null;
  showKey9Unlocked(result);
}

function onKey9Missed(result: Key9Result) {
  stopTimer();
  latestKey9Result = result;
  latestPracticeResult = null;
  const saved = currentPuzzle ? saveKey9Missed(currentPuzzle.date, result) : loadSave();
  latestProgress = currentPuzzle ? saved.days[currentPuzzle.date] : null;
  showKey9Missed(result);
}

function onKey9PracticeUnlocked(result: Key9Result) {
  stopTimer();
  latestPracticeResult = result;
  showPanel({
    title: 'Key9 Practice Complete',
    summary: `Practice run complete · Misses ${result.misses}/9 · ${formatTime(result.seconds)}`,
    status: 'Key9 Practice',
    doneCopy: '',
    inlineState: 'key9_practice_complete',
    sheetState: 'key9_missed',
    showPractice: true,
    practiceText: 'Try Again',
    showContinue: true,
    continueText: 'Continue Sudoku',
    shareText: 'Share Result',
    shareVariant: 'key9_practice_complete',
    key9Result: latestKey9Result,
    practiceResult: result,
    progress: latestProgress
  });
}

function onFullComplete(board: string[], seconds: number, notes?: string[], fullHints?: number) {
  stopTimer();
  if (!currentPuzzle) return;
  const saved = saveFullDone(currentPuzzle.date, board, seconds, notes, fullHints);
  latestProgress = saved.days[currentPuzzle.date];
  showFullDone(latestProgress, saved.currentStreak);
}

function showKey9Unlocked(result: Key9Result) {
  showPanel({
    title: 'Key9 Unlocked',
    summary: 'You placed 1–9. Now finish the Sudoku.',
    status: 'Key9 Unlocked',
    doneCopy: '',
    inlineState: 'key9_unlocked',
    sheetState: 'key9_unlocked',
    showPractice: false,
    practiceText: 'Try Again',
    showContinue: true,
    continueText: 'Continue Sudoku',
    shareText: 'Share Key9',
    shareVariant: 'key9_unlocked',
    key9Result: result,
    progress: latestProgress
  });
}

function showKey9Missed(result: Key9Result) {
  const copy = getMissedPanelCopy();
  showPanel({
    title: copy.title,
    summary: copy.body,
    status: 'Key9 Missed',
    doneCopy: '',
    inlineState: 'key9_missed',
    sheetState: 'key9_missed',
    showPractice: true,
    practiceText: copy.primaryButton,
    showContinue: true,
    continueText: copy.secondaryButton,
    shareText: copy.tertiaryButton,
    shareVariant: 'key9_missed',
    key9Result: result,
    progress: latestProgress
  });
}

function showFullDone(progress: DayProgress, streak?: number) {
  const key9 = progress.key9Result;
  const fullHints = progress.fullHints ?? 0;
  const fullCopy = fullHints > 0
    ? `Full Sudoku: Completed · Hints ${fullHints}`
    : 'Full Sudoku: Clean completion · Hints 0';
  const timeCopy = progress.fullSeconds ? ` · ${formatTime(progress.fullSeconds)}` : '';
  const streakCopy = streak && streak > 1 ? `\n${streak}-day streak` : '';
  const key9Copy = key9
    ? `Key9 result: ${resultLabel(key9.status)} · Misses ${key9.misses}/9`
    : 'Key9 result: Unlocked';
  showPanel({
    title: 'Today’s DaySudoku is done.',
    summary: `${key9Copy}\n${fullCopy}${timeCopy}${streakCopy}`,
    status: 'Done for today',
    doneCopy: 'You’re done for today.',
    inlineState: 'full_done',
    sheetState: 'full_done',
    showPractice: true,
    practiceText: 'Practice Again',
    showContinue: false,
    continueText: 'Continue Sudoku',
    shareText: 'Share Result',
    shareVariant: 'full_done',
    key9Result: key9,
    progress
  });
}

function showPanel(config: {
  title: string;
  summary: string;
  status: string;
  doneCopy: string;
  inlineState: InlineKey9ActionState;
  sheetState: ResultActionSheetState;
  showPractice: boolean;
  practiceText: string;
  showContinue: boolean;
  continueText: string;
  shareText: string;
  shareVariant: ShareCardVariant;
  key9Result?: Key9Result | null;
  practiceResult?: Key9Result | null;
  progress?: DayProgress | null;
}) {
  const panel = document.getElementById('ds-result');
  const card = document.querySelector('[data-game-card]');
  const title = document.getElementById('ds-result-title');
  const summary = document.getElementById('ds-result-summary');
  const doneCopy = document.getElementById('ds-done-copy');
  const practiceButton = document.getElementById('ds-practice') as HTMLButtonElement | null;
  const continueButton = document.getElementById('ds-continue') as HTMLButtonElement | null;
  const shareButton = document.getElementById('ds-share') as HTMLButtonElement | null;
  const inlineStrip = document.getElementById('ds-key9-actions');
  const inlineStatus = document.getElementById('ds-key9-actions-status');
  const inlinePracticeButton = document.getElementById('ds-inline-practice') as HTMLButtonElement | null;
  const inlineContinueButton = document.getElementById('ds-inline-continue') as HTMLButtonElement | null;
  const inlineShareButton = document.getElementById('ds-inline-share') as HTMLButtonElement | null;
  const sheet = document.getElementById('ds-action-sheet');
  const sheetTitle = document.getElementById('ds-sheet-title');
  const sheetSummary = document.getElementById('ds-sheet-summary');
  const sheetDoneCopy = document.getElementById('ds-sheet-done-copy');
  const sheetPracticeButton = document.getElementById('ds-sheet-practice') as HTMLButtonElement | null;
  const sheetContinueButton = document.getElementById('ds-sheet-continue') as HTMLButtonElement | null;
  const sheetShareButton = document.getElementById('ds-sheet-share') as HTMLButtonElement | null;
  if (title) title.textContent = config.title;
  if (summary) summary.textContent = config.summary;
  if (doneCopy) {
    doneCopy.textContent = config.doneCopy;
    doneCopy.hidden = config.doneCopy.length === 0;
  }
  if (practiceButton) {
    practiceButton.textContent = config.practiceText;
    practiceButton.hidden = !config.showPractice;
  }
  if (continueButton) {
    continueButton.textContent = config.continueText;
    continueButton.hidden = !config.showContinue;
  }
  if (shareButton) shareButton.textContent = config.shareText;
  latestShareVariant = config.shareVariant;
  renderStageShareCard(buildShareCardModel({
    date: currentPuzzle?.date ?? localDateKey(),
    variant: config.shareVariant,
    key9Result: config.key9Result ?? latestKey9Result,
    practiceResult: config.practiceResult ?? latestPracticeResult,
    progress: config.progress ?? latestProgress
  }));

  const inlineConfig = getInlineKey9Actions(config.inlineState);
  const layoutConfig = getTerminalResultLayout(config.inlineState);
  const inlineActionCount = Object.keys(inlineConfig.buttons).length;
  if (inlineStrip) {
    inlineStrip.hidden = !inlineConfig.visible;
    inlineStrip.dataset.state = inlineConfig.variant;
    inlineStrip.dataset.actionCount = String(inlineActionCount);
    inlineStrip.dataset.placement = layoutConfig.placement;
    inlineStrip.dataset.overlaysBoard = String(layoutConfig.overlaysBoard);
  }
  if (inlineStatus) inlineStatus.textContent = inlineConfig.status;
  if (inlinePracticeButton) {
    inlinePracticeButton.textContent = inlineConfig.buttons.practice ?? config.practiceText;
    inlinePracticeButton.hidden = !inlineConfig.buttons.practice;
    inlinePracticeButton.className = getInlineActionClass(inlineConfig.primaryAction, 'practice');
  }
  if (inlineContinueButton) {
    inlineContinueButton.textContent = inlineConfig.buttons.continue ?? config.continueText;
    inlineContinueButton.hidden = !inlineConfig.buttons.continue;
    inlineContinueButton.className = getInlineActionClass(inlineConfig.primaryAction, 'continue');
  }
  if (inlineShareButton) {
    inlineShareButton.textContent = inlineConfig.buttons.share ?? config.shareText;
    inlineShareButton.hidden = !inlineConfig.buttons.share;
    inlineShareButton.className = getInlineActionClass(inlineConfig.primaryAction, 'share');
  }
  panel?.toggleAttribute('data-inline-actions', inlineConfig.visible);

  if (sheetTitle) sheetTitle.textContent = config.title;
  if (sheetSummary) sheetSummary.textContent = config.summary;
  if (sheetDoneCopy) {
    sheetDoneCopy.textContent = config.sheetState === 'full_done' ? config.doneCopy : '';
    sheetDoneCopy.hidden = sheetDoneCopy.textContent.length === 0;
  }

  const sheetConfig = getResultActionSheet(config.sheetState);
  const actionCount = Object.keys(sheetConfig.buttons).length;
  if (sheet) {
    sheet.hidden = !sheetConfig.visible;
    sheet.dataset.state = sheetConfig.variant;
    sheet.dataset.actionCount = String(actionCount);
  }
  card?.classList.toggle('sheet-open', sheetConfig.visible);
  if (sheetPracticeButton) {
    sheetPracticeButton.textContent = sheetConfig.buttons.practice ?? config.practiceText;
    sheetPracticeButton.hidden = !sheetConfig.buttons.practice;
  }
  if (sheetContinueButton) {
    sheetContinueButton.textContent = sheetConfig.buttons.continue ?? config.continueText;
    sheetContinueButton.hidden = !sheetConfig.buttons.continue;
  }
  if (sheetShareButton) {
    sheetShareButton.textContent = sheetConfig.buttons.share ?? config.shareText;
    sheetShareButton.hidden = !sheetConfig.buttons.share;
  }
  setStatusLabel(config.status);
  updateGameControls(config.inlineState === 'full_done' ? 'full_done' : config.inlineState);
  if (panel) panel.hidden = false;
}

function hideResultPanel() {
  const panel = document.getElementById('ds-result');
  const inlineStrip = document.getElementById('ds-key9-actions');
  const sheet = document.getElementById('ds-action-sheet');
  const card = document.querySelector('[data-game-card]');
  if (panel) panel.hidden = true;
  panel?.removeAttribute('data-inline-actions');
  if (inlineStrip) inlineStrip.hidden = true;
  if (sheet) sheet.hidden = true;
  card?.classList.remove('sheet-open');
}

function renderStageShareCard(model: ShareCardModel) {
  const brand = document.getElementById('ds-stage-brand');
  const tagline = document.getElementById('ds-stage-tagline');
  const title = document.getElementById('ds-stage-result-title');
  const body = document.getElementById('ds-stage-result-body');
  const metrics = document.getElementById('ds-stage-metrics');
  const key9Label = document.getElementById('ds-stage-key9-label');
  const key9Cells = document.getElementById('ds-stage-key9-cells');
  const fullBadge = document.getElementById('ds-stage-full-badge');
  const footer = document.getElementById('ds-stage-footer');

  if (brand) brand.textContent = model.brand;
  if (tagline) tagline.textContent = model.tagline;
  if (title) title.textContent = model.title;
  if (body) body.textContent = model.body;
  if (key9Label) key9Label.textContent = model.key9Strip.label;
  if (footer) footer.textContent = model.footer;

  if (metrics) {
    metrics.replaceChildren();
    metrics.dataset.count = String(model.metrics.length);
    model.metrics.forEach((metric) => {
      const item = document.createElement('div');
      item.className = 'stage-metric';
      item.dataset.tone = metric.tone;
      const label = document.createElement('span');
      label.textContent = metric.label;
      const value = document.createElement('strong');
      value.textContent = metric.value;
      item.append(label, value);
      metrics.append(item);
    });
  }

  if (key9Cells) {
    key9Cells.replaceChildren();
    model.key9Strip.cells.forEach((state) => {
      const cell = document.createElement('span');
      cell.className = 'stage-key9-cell';
      cell.dataset.state = state;
      key9Cells.append(cell);
    });
  }

  if (fullBadge) {
    fullBadge.textContent = model.fullBadge.label;
    fullBadge.dataset.tone = model.fullBadge.tone;
  }
}

function getInlineActionClass(primaryAction: 'share' | null, action: 'practice' | 'continue' | 'share'): string {
  return primaryAction === action ? 'key9-primary' : 'key9-secondary';
}

function updateGameControls(phase: GameControlPhase, notesMode = false, canHint = false) {
  const controls = getGameControlState(phase);
  const actions = document.querySelector('[data-game-actions]') as HTMLElement | null;
  if (!actions) return;
  actions.hidden = !controls.visible;
  actions.dataset.mode = controls.mode;
  actions.dataset.placement = controls.placement;
  if (controls.layout) {
    actions.style.setProperty('--ds-toolbar-top', `${(controls.layout.toolbarTopY / 630) * 100}%`);
  } else {
    actions.style.removeProperty('--ds-toolbar-top');
  }
  setNotesButtonState(controls.visible && notesMode);
  setHintButtonState(controls.visible && canHint);
}

function setNotesButtonState(enabled: boolean) {
  const notesButton = document.getElementById('ds-notes') as HTMLButtonElement | null;
  notesButton?.setAttribute('aria-pressed', String(enabled));
}

function setHintButtonState(enabled: boolean) {
  const hintButton = document.getElementById('ds-hint') as HTMLButtonElement | null;
  if (!hintButton) return;
  if (enabled) {
    hintButton.removeAttribute('aria-disabled');
    hintButton.toggleAttribute('data-hint-ready', true);
    return;
  }

  hintButton.setAttribute('aria-disabled', 'true');
  hintButton.removeAttribute('data-hint-ready');
}

function setStatusLabel(label: string) {
  const statusLabel = document.getElementById('ds-status-label');
  if (statusLabel) statusLabel.textContent = label;
}

async function shareLatest() {
  if (!currentPuzzle) return;
  const save = loadSave();
  const text = latestShareVariant === 'full_done' && latestProgress?.phase === 'full_done'
    ? buildFullShareText(currentPuzzle.date, latestProgress, save.currentStreak)
    : latestShareVariant === 'key9_practice_complete' && latestPracticeResult
      ? buildPracticeShareText(currentPuzzle.date, latestPracticeResult, latestKey9Result, save.currentStreak)
      : latestKey9Result
        ? buildShareText(currentPuzzle.date, latestKey9Result, save.currentStreak, { progress: latestProgress })
        : '';
  if (!text) return;
  if (navigator.share) {
    await navigator.share({ text }).catch(() => undefined);
    return;
  }
  await navigator.clipboard?.writeText(text).catch(() => undefined);
  alert('DaySudoku result copied.');
}

boot();
